import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, MessageCircle, ChevronRight, ChevronDown, Loader2, Info } from 'lucide-react';
import { getProducts } from '../../lib/api';
import { getWhatsAppLink } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';
import FormModal from '../FormModal';

const statusStyles = {
  available: 'badge-available',
  coming_soon: 'badge-coming-soon',
  in_preparation: 'badge-preparation'
};

const statusLabels = {
  fr: { available: 'Disponible', coming_soon: 'Bientôt', in_preparation: 'Prochainement' },
  en: { available: 'Available', coming_soon: 'Coming soon', in_preparation: 'Coming next' }
};

const productTranslations = {
  fr: {
    'JoSmart Chat': {
      category: 'Communication',
      description: 'Une nouvelle génération de messagerie qui combine communication, réseau social et intelligence artificielle dans une seule application.',
      features: ['Messagerie rapide et sécurisée', 'Réseau social intégré', 'Intelligence artificielle (JoChat AI)', 'Traduction automatique français/anglais', 'Appels audio HD', 'Statuts éphémères 24h']
    },
    'JoSmart Edu': {
      category: 'Éducation',
      description: "Une application complète de gestion scolaire pour les écoles maternelles, primaires et secondaires. Notes, bulletins, présences, finances et communication — tout en un seul endroit.",
      features: ['Notes et bulletins automatiques', 'Suivi en temps réel pour les parents', 'Gestion des présences et de la discipline', 'Finances simplifiées', 'Communication directe école-parents', 'Site public pour votre école']
    },
    'JoSmart Academy': {
      category: 'Apprentissage',
      description: "Une plateforme d'apprentissage interactive de l'anglais, conçue pour les francophones. 6 niveaux progressifs du débutant à l'expert.",
      features: ['6 niveaux progressifs (A1 à C1)', 'Leçons courtes et pratiques', 'Exercices auto-corrigés', 'Audio et mises en situation', 'Accessible sur téléphone et ordinateur']
    },
    'JoSmart Market': {
      category: 'Marché virtuel',
      description: 'Une plateforme moderne pour connecter acheteurs et vendeurs dans un environnement simple, rapide et sécurisé.',
      features: ['Achat et vente en ligne', "Programme d'affiliation", 'Dropshipping intégré', 'Paiements sécurisés', 'Évaluations et avis']
    }
  },
  en: {
    'JoSmart Chat': {
      category: 'Communication',
      description: 'A new generation messaging app that combines communication, social networking and artificial intelligence in one application.',
      features: ['Fast and secure messaging', 'Integrated social network', 'Artificial intelligence (JoChat AI)', 'Automatic French/English translation', 'HD audio calls', '24h ephemeral statuses']
    },
    'JoSmart Edu': {
      category: 'Education',
      description: 'A complete school management app for kindergarten, primary and secondary schools. Grades, report cards, attendance, finances and communication — all in one place.',
      features: ['Automatic grades and report cards', 'Real-time tracking for parents', 'Attendance and discipline management', 'Simplified finances', 'Direct school-parent communication', 'Public website for your school']
    },
    'JoSmart Academy': {
      category: 'Learning',
      description: 'An interactive English learning platform designed for French speakers. 6 progressive levels from beginner to expert.',
      features: ['6 progressive levels (A1 to C1)', 'Short and practical lessons', 'Self-correcting exercises', 'Audio and real-life scenarios', 'Accessible on phone and computer']
    },
    'JoSmart Market': {
      category: 'Virtual marketplace',
      description: 'A modern platform to connect buyers and sellers in a simple, fast and secure environment.',
      features: ['Online buying and selling', 'Affiliate program', 'Integrated dropshipping', 'Secure payments', 'Ratings and reviews']
    }
  }
};

const defaultProducts = [
  { id: '1', name: 'JoSmart Chat', status: 'coming_soon' },
  { id: '2', name: 'JoSmart Edu', status: 'available', download_link: 'https://edu.josmarttech.net' },
  { id: '3', name: 'JoSmart Academy', status: 'available', download_link: 'https://academy.josmarttech.net' },
  { id: '4', name: 'JoSmart Market', status: 'coming_soon' }
];

export const ProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formProduct, setFormProduct] = useState('');
  const { t, language } = useLanguage();

  const getStatusLabel = (status) => statusLabels[language][status] || status;

  const getProductTranslation = (productName) => {
    return productTranslations[language][productName] || productTranslations['fr'][productName] || {};
  };

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(data && data.length > 0 ? data : defaultProducts);
    } catch (err) {
      setProducts(defaultProducts);
    } finally {
      setLoading(false);
    }
  };

  const toggleProduct = (id) => {
    setExpandedProduct(expandedProduct === id ? null : id);
  };

  const openFormForProduct = (productName) => {
    setFormProduct(productName);
    setShowFormModal(true);
  };

  return (
    <>
      <section id="products" className="py-24 bg-[#152840]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#4A90D9]/20 text-[#4A90D9] text-sm font-medium tracking-wide mb-4">
              {t('products_badge')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {t('products_title')}
            </h2>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 size={40} className="animate-spin text-[#4A90D9]" /></div>
          ) : (
            <div className="space-y-4">
              {products.map((product, index) => {
                const isExpanded = expandedProduct === product.id;
                const translation = getProductTranslation(product.name);
                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {/* Product Header - Clickable */}
                    <button
                      onClick={() => toggleProduct(product.id)}
                      className={`w-full flex items-center gap-5 p-6 rounded-2xl transition-all duration-300 group cursor-pointer ${
                        isExpanded
                          ? 'bg-[#4A90D9] text-white'
                          : 'bg-[#0B1521]/70 text-white/90 hover:bg-[#0B1521] border border-white/10 hover:border-[#4A90D9]/40'
                      }`}
                      data-testid={`product-btn-${product.id}`}
                    >
                      <div className="flex-1 text-left">
                        <div className="flex items-center gap-3">
                          <h3 className="text-xl font-bold">{product.name}</h3>
                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${isExpanded ? 'bg-white/20 text-white' : statusStyles[product.status]}`}>
                            {getStatusLabel(product.status)}
                          </span>
                        </div>
                        <p className={`text-sm mt-1 ${isExpanded ? 'text-white/80' : 'text-white/50'}`}>
                          {translation.category}
                        </p>
                      </div>
                      <div className={`flex items-center gap-2 text-sm font-medium ${isExpanded ? 'text-white/80' : 'text-[#4A90D9]'}`}>
                        <span className="hidden sm:inline">{isExpanded ? (language === 'fr' ? 'Fermer' : 'Close') : (language === 'fr' ? 'Voir les détails' : 'See details')}</span>
                        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <ChevronDown size={20} />
                        </motion.div>
                      </div>
                    </button>

                    {/* Expandable Details */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="bg-[#0B1521] rounded-2xl p-8 mt-2 border border-white/10" data-testid="product-details">
                            <p className="text-white/70 text-lg leading-relaxed mb-6">{translation.description}</p>

                            <div className="mb-6">
                              <h4 className="text-white font-semibold mb-4">{t('products_features')}</h4>
                              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {(translation.features || []).map((feature, i) => (
                                  <li key={i} className="flex items-center gap-2 text-white/80 text-sm">
                                    <ChevronRight size={14} className="text-[#4A90D9] flex-shrink-0" />
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Actions */}
                            <div className="flex flex-wrap gap-3 pt-4 border-t border-white/10">
                              {product.status === 'available' && product.download_link && (
                                <a
                                  href={product.download_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 px-6 py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors"
                                  data-testid="product-download"
                                >
                                  <Download size={18} />
                                  {t('products_download')}
                                </a>
                              )}
                              <button
                                onClick={() => openFormForProduct(product.name)}
                                className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full font-semibold hover:bg-white/20 transition-colors border border-white/20"
                                data-testid="product-contact-btn"
                              >
                                <Info size={18} />
                                {t('products_more')}
                              </button>
                              <a
                                href={getWhatsAppLink(language === 'en' ? `Hello, I'm interested in ${product.name}.` : `Bonjour, je suis intéressé par ${product.name}.`)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-semibold hover:bg-[#20BD5A] transition-colors"
                                data-testid="product-whatsapp"
                              >
                                <MessageCircle size={18} />
                                {t('whatsapp')}
                              </a>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <FormModal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        formType="product"
        title={`${language === 'fr' ? 'En savoir plus' : 'Learn more'} - ${formProduct}`}
      />
    </>
  );
};

export default ProductsSection;
